import { useState } from "react";
import { Mail, Send, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";

const Contact = ({ onSubmit }) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await onSubmit(formData);
      setSubmitted(true);
      setFormData({ name: "", email: "", subject: "", message: "" });
    } catch (e) {
      console.error("Contact form submission failed:", e);
    }
    setIsSubmitting(false);
  };

  const contactInfo = [
    {
      icon: Mail,
      title: "Email",
      content: "deepa.sa@gmail.com",
      href: "mailto:deepa.sa@gmail.com",
    },
    {
      icon: MapPin,
      title: "Location",
      content: "Sydney, Australia",
    },
    {
      icon: Clock,
      title: "Response Time",
      content: "Within 24-48 hours",
    },
  ];

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="py-20 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="uppercase tracking-[0.2em] text-xs font-semibold text-primary">
            Get in Touch
          </span>
          <h2 className="mt-4 text-3xl md:text-4xl font-bold tracking-tight">
            Let's Connect
          </h2>
          <p className="mt-4 text-muted-foreground max-w-2xl mx-auto">
            Have questions about coaching or want to discuss how I can help you? 
            Send me a message and I'll get back to you soon.
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-12">
          {/* Contact Info */}
          <div className="space-y-6">
            {contactInfo.map((item, index) => (
              <div
                key={index}
                data-testid={`contact-info-${index}`}
                className="flex items-start gap-4"
              >
                <div className="p-3 bg-primary/10 rounded-lg">
                  <item.icon className="h-5 w-5 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">{item.title}</h3>
                  {item.href ? (
                    <a
                      href={item.href}
                      className="text-muted-foreground hover:text-primary transition-colors"
                    >
                      {item.content}
                    </a>
                  ) : (
                    <p className="text-muted-foreground">{item.content}</p>
                  )}
                </div>
              </div>
            ))}

            {/* Additional Info */}
            <div className="p-6 bg-muted/50 rounded-xl mt-8">
              <h3 className="font-semibold mb-2">Prefer to Book Directly?</h3>
              <p className="text-sm text-muted-foreground mb-4">
                Skip the contact form and schedule a free discovery call.
              </p>
              <Button
                data-testid="contact-book-btn"
                onClick={() => {
                  const element = document.querySelector("#booking");
                  if (element) element.scrollIntoView({ behavior: "smooth" });
                }}
                variant="outline"
                size="sm"
              >
                Book Discovery Call
              </Button>
            </div>
          </div>

          {/* Contact Form */}
          <div className="md:col-span-2">
            {submitted ? (
              <Card className="border-border/50">
                <CardContent className="pt-8 pb-6 text-center">
                  <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Mail className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold mb-2">Message Sent!</h3>
                  <p className="text-muted-foreground mb-6">
                    Thank you for reaching out. I'll get back to you within 24-48 hours.
                  </p>
                  <Button
                    data-testid="contact-another-btn"
                    onClick={() => setSubmitted(false)}
                    variant="outline"
                  >
                    Send Another Message
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <Card className="border-border/50">
                <CardHeader>
                  <CardTitle>Send a Message</CardTitle>
                  <CardDescription>
                    Fill out the form below and I'll respond as soon as possible.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <form onSubmit={handleSubmit} className="space-y-5">
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="contact-name">Your Name *</Label>
                        <Input
                          id="contact-name"
                          name="name"
                          data-testid="contact-name-input"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="Your full name"
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="contact-email">Email Address *</Label>
                        <Input
                          id="contact-email"
                          name="email"
                          type="email"
                          data-testid="contact-email-input"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="your@email.com"
                          required
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-subject">Subject</Label>
                      <Input
                        id="contact-subject"
                        name="subject"
                        data-testid="contact-subject-input"
                        value={formData.subject}
                        onChange={handleInputChange}
                        placeholder="What is this regarding?"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="contact-message">Your Message *</Label>
                      <Textarea
                        id="contact-message"
                        name="message"
                        data-testid="contact-message-input"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="How can I help you?"
                        rows={5}
                        required
                      />
                    </div>

                    <Button
                      type="submit"
                      data-testid="contact-submit-btn"
                      disabled={isSubmitting}
                      className="w-full btn-primary"
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <div className="spinner w-4 h-4" />
                          Sending...
                        </span>
                      ) : (
                        <>
                          Send Message
                          <Send className="ml-2 h-4 w-4" />
                        </>
                      )}
                    </Button>
                  </form>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
